import React, { useEffect, useState } from 'react';
import { useCommandPaletteShortcut } from '@atlassian/jira-command-palette-common/src/controllers/use-command-palette-shortcut/index.tsx';
import { useCommandPaletteIsOpen } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import type { CommandPaletteModalProps } from '@atlassian/jira-command-palette/src/ui/modal/types.tsx';
import { CommandPaletteWrapper } from '@atlassian/jira-command-palette/src/ui/wrapper/index.tsx';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import usePageLoadedSubscriber from '@atlassian/jira-spa-state-controller/src/components/page-loaded-state/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { JiraCommandPaletteErrorBoundary } from './common/ui/error-boundary/index.tsx';

/* istanbul ignore next */
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const JiraCommandPaletteLazy = lazy(() =>
	import(/* webpackChunkName: "async-command-palette-integration" */ './ui').then(
		(module) => module.CommandPaletteIntegration,
	),
);

const InternalAsyncCommandPalette = (props: CommandPaletteModalProps) => {
	const [initialised, setInitialised] = useState(false);

	const [{ isOpen }] = useCommandPaletteIsOpen();
	const { keyMap: commandPaletteKeyMap } = useCommandPaletteShortcut();

	const [{ hasLoaded }] = usePageLoadedSubscriber();

	useEffect(() => {
		if (!initialised && (isOpen || hasLoaded)) setInitialised(true);
	}, [initialised, isOpen, hasLoaded]);

	return (
		<UFOSegment name="command-palette">
			<Shortcuts keyMap={commandPaletteKeyMap} />

			{initialised && (
				<Placeholder name="jira-command-palette" fallback={<></>}>
					<JiraCommandPaletteLazy {...props} />
				</Placeholder>
			)}
		</UFOSegment>
	);
};

export const AsyncJiraCommandPalette = (props: CommandPaletteModalProps): React.JSX.Element => (
	<JiraCommandPaletteErrorBoundary id="jira-command-palette-integration-async">
		<CommandPaletteWrapper>
			<InternalAsyncCommandPalette {...props} />
		</CommandPaletteWrapper>
	</JiraCommandPaletteErrorBoundary>
);
